/**
 * Reading the term map: one small object, built once.
 *
 * Every organisation reads the same words, so there is nothing to load and
 * nothing to wait for. React code calls `useVocab()` and other code calls
 * `vocab()`; both return the same object.
 */
import { TERMS, type Forms, type Noun, type Phrases } from './terms';

export type Vocab = {
  one: (noun: Noun) => string;
  many: (noun: Noun) => string;
  One: (noun: Noun) => string;
  Many: (noun: Noun) => string;
  /** With its article: "a row", "an endpoint". */
  a: (noun: Noun) => string;
  A: (noun: Noun) => string;
  /** The singular or the plural, whichever `n` takes: "row" for 1. */
  word: (n: number, noun: Noun) => string;
  /** A number and its noun: "1 row", "1,204 rows". */
  count: (n: number, noun: Noun) => string;
  forms: (noun: Noun) => Forms;
  /** Words that do not compose from the nouns. */
  p: Phrases;
};

const plural = new Intl.PluralRules('en-GB');
const number = new Intl.NumberFormat('en-GB');

const word = (n: number, noun: Noun) => (plural.select(n) === 'one' ? TERMS[noun].one : TERMS[noun].many);

const VOCAB: Vocab = {
  one: (noun) => TERMS[noun].one,
  many: (noun) => TERMS[noun].many,
  One: (noun) => TERMS[noun].One,
  Many: (noun) => TERMS[noun].Many,
  a: (noun) => TERMS[noun].a,
  A: (noun) => TERMS[noun].A,
  word,
  count: (n, noun) => `${number.format(n)} ${word(n, noun)}`,
  forms: (noun) => TERMS[noun],
  p: TERMS.phrases,
};

/** The words, for code that is not a component. */
export function vocab(): Vocab {
  return VOCAB;
}

/**
 * The words, for a component. Kept under its hook name so every call site
 * reads as it did before the second word set was removed.
 */
export function useVocab(): Vocab {
  return VOCAB;
}
